import { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { EventTypeCellParts, EventTypeStrategy } from "./EventTypeStrategy";
import { getTranslatedEventTypeName } from "../../utils/eventTypeUtils";

import { Button } from "@/components/ui/v2/button";
import { TransactionType } from "@/types/transaction/TransactionType";
import { formatCurrency } from "@/utils/valueFormat";

export class CampaignInvoicedEventTypeStrategy implements EventTypeStrategy {
  renderCell(row: TransactionType, t: TFunction): JSX.Element {
    const forText = row.eventData.campaignName ? (
      <>
        {" "}
        {t("for")} <b>{row.eventData.campaignName}</b>
      </>
    ) : (
      ""
    );
    const onText = row.eventData.jobBoardName ? (
      <>
        {" "}
        {t("on")} <b>{row.eventData.jobBoardName}</b>
      </>
    ) : (
      ""
    );
    const inText = row.eventData.month ? (
      <>
        {" "}
        {t("in")}{" "}
        <b>
          {new Date(row.eventData.month).toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </b>
      </>
    ) : (
      ""
    );
    const allocationReleasedText = row.eventData.allocatedAmount ? (
      <>
        {" | "}
        {t("common.allocation")} {t("common.released")}:{" "}
        <b>{formatCurrency(row.eventData.allocatedAmount)}</b>
      </>
    ) : (
      ""
    );

    return (
      <>
        {t(`eventTypes.${row.eventType}`)}
        {forText}
        {onText}
        {inText}
        {allocationReleasedText}
      </>
    );
  }

  getTypeAndDescription(
    row: TransactionType,
    t: TFunction
  ): EventTypeCellParts {
    const campaignName = row.eventData.campaignName || "";
    const jobBoardName = row.eventData.jobBoardName || "";
    const campaignId = row.eventData.campaignId;
    const clientIntegrationModuleId =
      row.eventData.clientIntegrationModuleId ?? row.eventData.integrationModuleId ?? row.eventData.jobBoardId;
    const clientId = row.eventData.clientId || row.clientId2;
    const month = row.eventData.month;
    const parts: (string | JSX.Element)[] = [];

    // "for [Campaign Name (link)]" - Start with "for" to match ticket example
    if (campaignName && campaignId && clientId) {
      parts.push(
        <>
          {t("common.for")}{" "}
          <Button asChild variant={"link"} className="px-0 ">
            <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
              {campaignName}
            </Link>
          </Button>
        </>
      );
    } else if (campaignName) {
      parts.push(
        <>
          {t("common.for")} <b>{campaignName}</b>
        </>
      );
    } else if (campaignId && clientId) {
      parts.push(
        <>
          {t("common.for")}{" "}
          <Button asChild variant={"link"} className="px-0">
            <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
              {campaignId}
            </Link>
          </Button>
        </>
      );
    } else if (campaignId) {
      parts.push(
        <>
          {t("common.for")} <b>{campaignId}</b>
        </>
      );
    }

    // "on [Job Board Name (link)]"
    if (jobBoardName && clientIntegrationModuleId && campaignId && clientId) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.on")}{" "}
          <Button asChild variant={"link"} className="px-0">
            <Link
              to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
            >
              {jobBoardName}
            </Link>
          </Button>
        </>
      );
    } else if (jobBoardName) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.on")} <b>{jobBoardName}</b>
        </>
      );
    } else if (clientIntegrationModuleId && campaignId && clientId) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.on")}{" "}
          <Button asChild variant={"link"} className="px-0">
            <Link
              to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
            >
              {clientIntegrationModuleId}
            </Link>
          </Button>
        </>
      );
    } else if (clientIntegrationModuleId) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.on")} <b>{clientIntegrationModuleId}</b>
        </>
      );
    }

    // "in [Month Year]"
    if (month) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.in")}{" "}
          <b>
            {new Date(month).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </b>
        </>
      );
    }

    // Allocation Released - Add separator before if there are previous parts
    if (
      row.eventData.allocatedAmount !== null &&
      row.eventData.allocatedAmount !== undefined
    ) {
      if (parts.length > 0) parts.push(" | ");
      parts.push(
        <>
          {t("common.allocation")} {t("common.released")}:{" "}
          <b>{formatCurrency(row.eventData.allocatedAmount)}</b>
        </>
      );
    }

    return {
      type: <>{getTranslatedEventTypeName(row.eventType, t)}</>,
      description: <>{parts}</>,
    };
  }
}
